import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plane, 
  Train, 
  Bus, 
  Car, 
  Compass, 
  ChevronRight, 
  ChevronDown 
} from 'lucide-react';
import TravelDetailsModal from './modals/TravelDetailsModal';

// Helper to format booking dates cleanly as "12 Nov"
const formatBookingDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const formatModeLabel = (mode) => {
  if (!mode) return 'Travel';
  return mode.charAt(0).toUpperCase() + mode.slice(1);
};

const TravelBookingsCard = ({ voucher, onUpdateVoucher }) => {
  const navigate = useNavigate();
  const travels = voucher?.travel_bookings || [];

  // Individual Travel Edit Modal state
  const [selectedTravelData, setSelectedTravelData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // View All Travels Modal state
  const [showAllTravels, setShowAllTravels] = useState(false);

  // Prevent background scrolling and enable Escape close when "View All Travels" modal is active
  useEffect(() => {
    if (!showAllTravels) return;
    const prevBodyOverflow = document.body.style.overflow;
    const mainEl = document.querySelector('main');
    const prevMainOverflow = mainEl ? mainEl.style.overflow : '';

    document.body.style.overflow = 'hidden';
    if (mainEl) mainEl.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowAllTravels(false);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      if (mainEl) mainEl.style.overflow = prevMainOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAllTravels]);

  const handleOpenTravelModal = (travel, index) => {
    setSelectedTravelData({ travel, index });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTravelData(null);
  };

  const handleSaveTravel = async (travelIndex, updatedTravel) => {
    const updatedTravels = [...travels];
    updatedTravels[travelIndex] = updatedTravel;

    const updatedVoucher = {
      ...voucher,
      travel_bookings: updatedTravels
    };

    if (onUpdateVoucher) {
      await onUpdateVoucher(updatedVoucher);
    }

    // Keep active modal updated
    setSelectedTravelData({ travel: updatedTravel, index: travelIndex });
  };

  const handleTrack = (e, t) => {
    e.stopPropagation();
    const mode = (t.mode_of_transport || t.mode || t.type || 'flight').toLowerCase();
    const num = t.vehicle_number || t.flight_number || t.identifier || '';
    if (!num) return;

    setShowAllTravels(false);

    if (mode === 'train' || mode.includes('rail')) {
      navigate(`/tracking/train-tracking?train_number=${num}&journey_date=${t.journey_date || ''}`);
    } else if (mode === 'flight' || mode === 'plane' || mode.includes('flight') || mode.includes('air')) {
      navigate(`/tracking/flight-tracking?flight_number=${num}`);
    } else {
      window.open(`https://www.google.com/search?q=track+${encodeURIComponent(t.operator || '')}+${encodeURIComponent(num)}`, '_blank');
    }
  };

  return (
    <>
      <div className="vc-card flex flex-col justify-between">
        <div>
          {/* Header */}
          <div className="vc-card-head flex items-center justify-between pb-2 border-b border-slate-100">
            <h3 className="flex items-center gap-2 text-sm font-medium text-slate-600 tracking-tight">
              <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                <Plane size={15} className="text-[#067978]" />
              </span>
              <span>Travel Bookings</span>
            </h3>
          </div>

          {/* Travel Rows: Matches DMC layout */}
          <div className="divide-y divide-slate-100 mt-1">
            {travels.length > 0 ? (
              travels.slice(0, 3).map((travel, idx) => {
                const travelKey = travel._id || travel.id || idx;
                const travelIdentifier = travel.vehicle_number || travel.flight_number || travel.identifier || '';
                const travelTitle = travel.operator
                  ? `${travel.operator}${travelIdentifier ? ` · ${travelIdentifier}` : ''}`
                  : (travelIdentifier ? `${formatModeLabel(travel.mode_of_transport)} · ${travelIdentifier}` : `Travel Entry #${idx + 1}`);
                
                const formattedDate = formatBookingDate(travel.journey_date);
                const timeStr = travel.departure_time || '10:00';
                const dateTimeString = formattedDate ? `${formattedDate} · ${timeStr}` : timeStr;
                const modeLabel = formatModeLabel(travel.mode_of_transport || travel.mode || travel.type);
                const routeString = travel.from && travel.to ? `${travel.from} → ${travel.to}` : (travel.from || travel.to || '');

                return (
                  <div
                    key={travelKey}
                    onClick={() => handleOpenTravelModal(travel, idx)}
                    className="flex items-center justify-between py-2.5 px-0 hover:bg-slate-50/50 transition-colors cursor-pointer group select-none"
                    title="Click to view & edit travel details"
                  >
                    <div className="min-w-0 pr-4">
                      <div className="text-[13px] sm:text-[14px] font-medium text-slate-700 group-hover:text-[#067978] transition-colors truncate">
                        {travelTitle}
                      </div>
                      <div className="text-xs text-slate-500 font-normal mt-0.5 truncate">
                        {routeString || modeLabel || 'Travel details'}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-slate-500 font-normal">
                        {dateTimeString}
                      </span>
                      <ChevronDown size={16} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-6 text-center text-xs text-slate-400">
                No travel bookings attached to this voucher.
              </div>
            )}
          </div>
        </div>

        {/* View travels footer cue: "View 2 travels" (no "all", no "journeys") */}
        {travels.length > 0 && (
          <div 
            className="vc-expand-cue mt-auto pt-3 flex items-center justify-center gap-1 text-xs font-normal text-[#067978] cursor-pointer hover:opacity-80 select-none"
            onClick={(e) => {
              e.stopPropagation();
              setShowAllTravels(true);
            }}
            title="View travels in full modal"
          >
            <span>{`View ${travels.length} ${travels.length === 1 ? 'travel' : 'travels'}`}</span>
            <ChevronDown size={14} className="text-[#067978]" />
          </div>
        )}

        {/* Individual Travel Details & Edit Modal */}
        <TravelDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          travel={selectedTravelData?.travel}
          travelIndex={selectedTravelData?.index}
          onSaveTravel={handleSaveTravel}
        />
      </div>

      {/* View All Travels Modal - ONLY rendered when travels exist */}
      {typeof document !== 'undefined' && travels.length > 0 && createPortal(
        <div
          className={`vc-overlay ${showAllTravels ? 'vc-open' : ''}`}
          style={{ top: 'var(--portal-header-height, 64px)', height: 'calc(100vh - var(--portal-header-height, 64px))' }}
          onClick={(e) => {
            if (e.target.classList.contains('vc-overlay')) setShowAllTravels(false);
          }}
        >
          <div className="vc-modal !max-w-2xl" role="dialog" aria-modal="true" aria-label="Travel bookings">
            <div className="vc-m-head">
              <h4 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Plane size={18} className="text-[#067978]" /> Travel Bookings
              </h4>
              <button className="vc-m-close" onClick={() => setShowAllTravels(false)}>✕</button>
            </div>
            <div className="vc-m-body space-y-3">
              {travels.map((t, i) => {
                const mode = (t.mode_of_transport || t.mode || t.type || 'flight').toLowerCase();
                const identifier = t.vehicle_number || t.flight_number || t.identifier || '';
                const operator = t.operator || '';
                const title = operator ? `${operator}${identifier ? ` · ${identifier}` : ''}` : (identifier || `${formatModeLabel(mode)} #${i + 1}`);
                const hasTracking = Boolean(identifier && identifier.trim() !== '');

                return (
                  <div
                    key={i}
                    onClick={() => {
                      setShowAllTravels(false);
                      handleOpenTravelModal(t, i);
                    }}
                    className="p-4 rounded-2xl bg-white border border-[#e7ecea] hover:border-teal-200/80 shadow-[0_2px_8px_rgba(16,38,58,0.04)] hover:shadow-[0_4px_16px_rgba(16,38,58,0.08)] transition-all cursor-pointer group"
                    title="Click to view & edit travel details"
                  >
                    {/* Top Row: Mode icon, Title, Actions */}
                    <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-100">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-[#067978] shrink-0">
                          {mode === 'train' || mode.includes('rail') ? (
                            <Train size={15} />
                          ) : mode === 'bus' ? (
                            <Bus size={15} />
                          ) : mode === 'cab' || mode === 'car' ? (
                            <Car size={15} />
                          ) : (
                            <Plane size={15} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 group-hover:text-[#067978] transition-colors truncate">
                            {title}
                          </div>
                          <div className="text-[11px] text-slate-500 font-normal capitalize mt-0.5">
                            {mode} Journey{t.travel_class ? ` · ${t.travel_class}` : ''}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {hasTracking && (
                          <button
                            type="button"
                            onClick={(e) => handleTrack(e, t)}
                            className="px-2.5 py-1 text-xs font-normal text-[#067978] bg-teal-50 hover:bg-teal-100/80 border border-teal-200/60 rounded-lg transition-all flex items-center gap-1 cursor-pointer active:scale-95"
                            title="Track live status"
                          >
                            <Compass size={12} />
                            <span>Track</span>
                          </button>
                        )}
                        <span className="text-xs font-normal text-slate-400 group-hover:text-[#067978] transition-colors flex items-center gap-0.5">
                          <span>Details</span>
                          <ChevronRight size={13} />
                        </span>
                      </div>
                    </div>

                    {/* Route row: From -> To with clean indicators */}
                    <div className="mt-3 flex items-center justify-between gap-4 text-xs">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase font-normal text-slate-400 tracking-wider">From</div>
                        <div className="font-medium text-slate-700 truncate mt-0.5" title={t.from}>
                          {t.from || 'Origin'}
                        </div>
                        {t.departure_time && (
                          <div className="text-[11px] font-mono text-teal-700 mt-0.5">
                            {t.departure_time}
                          </div>
                        )}
                      </div>

                      <div className="shrink-0 flex flex-col items-center justify-center px-2">
                        <span className="text-slate-300 text-xs font-normal">→</span>
                        {t.journey_date && (
                          <span className="text-[10px] text-slate-400 whitespace-nowrap mt-0.5">
                            {formatBookingDate(t.journey_date)}
                          </span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1 text-right">
                        <div className="text-[10px] uppercase font-normal text-slate-400 tracking-wider">To</div>
                        <div className="font-medium text-slate-700 truncate mt-0.5" title={t.to}>
                          {t.to || 'Destination'}
                        </div>
                        {t.arrival_time && (
                          <div className="text-[11px] font-mono text-teal-700 mt-0.5">
                            {t.arrival_time}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer info: PNR, Seat, Operator */}
                    {(t.pnr || t.seat_class || t.seat_no || t.departure_terminal || t.driver_name) && (
                      <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                        {t.pnr && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 font-normal">PNR:</span>
                            <span className="font-mono font-medium text-slate-700 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                              {t.pnr}
                            </span>
                          </div>
                        )}
                        {(t.seat_class || t.seat_no) && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 font-normal">Class:</span>
                            <span className="font-normal text-slate-600">{t.seat_class || t.seat_no}</span>
                          </div>
                        )}
                        {t.departure_terminal && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 font-normal">Terminal:</span>
                            <span className="font-normal text-slate-600">{t.departure_terminal}</span>
                          </div>
                        )}
                        {t.driver_name && (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 font-normal">Driver:</span>
                            <span className="font-normal text-slate-600">{t.driver_name}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TravelBookingsCard;
